import React, { useEffect, useState } from 'react';
import { getSnapshotValidation } from 'api/snapshotValidationApi';
import {
  subscribeToAnalyzers,
  updateAnalyzerProperties,
} from '../spectrasWebSocketServer/spectrasWebSocketServerSlice';
import { useIsViewer } from '../auth/hooks/useIsViewer';
import { snapshotChanged } from '../snapshots/snapshotsSlice';
import {
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  Tooltip,
} from '@mui/material';

import AnalyzerModeBar from './AnalyzerModeBar';
import AnalyzerSelect from './AnalyzerSelect';
import { Analyzer, Snapshot } from 'types';
import { AnalyzerMode } from './types';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { LockOutlined } from '@mui/icons-material';

import { useAnalyzer } from './api/getAnalyzer';
import { useSnapshots } from 'features/snapshots/api/getSnapshots';
import { useUpdateAnalyzer } from './api/updateAnalyzer';
import { useUpdateSnapshot } from 'features/snapshots/api/updateSnapshot';
import { useIsGeoEnabled } from 'features/analyzerModules/api/getAnalyzerModules';
import { useSnapshotFilterBar } from 'features/snapshots/SnapshotFilterBar';
import TotalSweepTime from 'features/snapshots/TotalSweepTime';
import SyncIcon from '@mui/icons-material/Sync';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import SingleTraceViewDialog from 'features/searchWidget/SingleTraceViewDialog';

interface Props {
  analyzers: Analyzer[] | undefined;
  selectedAnalyzerId: number;
  selectedSnapshotId: number;
  useModuleSelectors: boolean;
  disableAnalyzerSelect?: boolean;
  setValidatedSnapshotProperties: (...args: any) => void;
}

const AnalyzerSnapshotsSelector = ({
  analyzers,
  selectedAnalyzerId,
  selectedSnapshotId,
  useModuleSelectors,
  disableAnalyzerSelect,
  setValidatedSnapshotProperties,
}: Props) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [singleTraceViewModalOpen, setSingleTraceViewModalOpen] =
    useState<boolean>(false);
  const [singleTraceViewSnapshotId, setSingleTraceViewSnapshotShotId] =
    useState<number>(-1);

  const isViewer = useIsViewer();

  const { data: analyzer } = useAnalyzer({ analyzerId: selectedAnalyzerId });
  const updateAnalyzerMutation = useUpdateAnalyzer({});
  const updateSnapshotMutation = useUpdateSnapshot({});

  const { data: snapshots } = useSnapshots({ analyzerId: selectedAnalyzerId });

  const temporarilyDisabledSnapshots = snapshots?.filter(
    (snapshot) => Boolean(snapshot.temporarily_disabled) === true
  );
  const snapshotIsolated =
    temporarilyDisabledSnapshots && temporarilyDisabledSnapshots.length > 0;

  const handleSnapshotClicked = async (snapshot: Snapshot) => {
    setValidatedSnapshotProperties(null);
    dispatch(snapshotChanged(snapshot.id));
    const validation = await getSnapshotValidation(snapshot.id);
    setValidatedSnapshotProperties(validation);
  };

  useEffect(() => {
    dispatch(snapshotChanged(-1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAnalyzerId]);

  const isGeoEnabled = useIsGeoEnabled(selectedAnalyzerId);

  const { SnapshotFilterBar, getFilterSnapshotsByName } =
    useSnapshotFilterBar();
  const filterSnapshots = getFilterSnapshotsByName(snapshots || []);

  const handleSnapshotChecked = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!snapshots || !user) return;
    const { name, checked } = event.target;
    const selectedSnapshot = snapshots.find(
      (snapshot) => snapshot.id === parseInt(name)
    );
    if (!selectedSnapshot) return;
    const { baseline_trace, ...editedSnapshot } = selectedSnapshot;

    if (analyzer?.mode === AnalyzerMode.LIVE) {
      await updateAnalyzerMutation.mutateAsync({
        data: {
          ...analyzer,
          active_snapshot: Number(name),
          enabled: !!analyzer.enabled,
        },
        analyzerId: analyzer.id,
      });

      dispatch(
        updateAnalyzerProperties({
          user_id: user.id,
          analyzer: {
            ...analyzer,
            active_snapshot: Number(name),
          },
          properties: editedSnapshot,
        })
      );
    } else if (analyzer?.mode === AnalyzerMode.POLLING) {
      if (!isGeoEnabled) return;
      await updateSnapshotMutation.mutateAsync({
        data: {
          ...editedSnapshot,
          enabled: checked,
        },
        snapshotId: editedSnapshot.id,
      });
      if (temporarilyDisabledSnapshots && snapshotIsolated) {
        for (let i = 0; i < temporarilyDisabledSnapshots.length; i++) {
          if (editedSnapshot.id !== temporarilyDisabledSnapshots[i].id)
            await updateSnapshotMutation.mutateAsync({
              data: {
                ...temporarilyDisabledSnapshots[i],
                temporarily_disabled: false,
              },
              snapshotId: temporarilyDisabledSnapshots[i].id,
            });
        }

        return;
      }
    }
  };

  const checkIfSnapshotChecked = (snapshot: Snapshot) => {
    if (analyzer?.mode === 0) {
      return analyzer.active_snapshot === snapshot.id;
    } else if (analyzer?.mode === 1) {
      return !!snapshot.enabled;
    }
  };

  return (
    <>
      <SingleTraceViewDialog
        open={singleTraceViewModalOpen}
        snapshotId={singleTraceViewSnapshotId}
        setSnapshotId={setSingleTraceViewSnapshotShotId}
        analyzerId={selectedAnalyzerId}
        traceMode={0}
        handleClose={() => setSingleTraceViewModalOpen(false)}
      />
      <Grid
        item
        container
        direction='column'
        spacing={1}
        style={{ margin: 0, height: '100%', paddingRight: '1.7rem' }}
      >
        {!disableAnalyzerSelect && (
          <Grid item container justifyContent='space-between'>
            <Grid item>
              <AnalyzerSelect
                analyzers={analyzers}
                selectedAnalyzerId={selectedAnalyzerId}
              />
            </Grid>
            <Grid item>{SnapshotFilterBar}</Grid>
          </Grid>
        )}
        {useModuleSelectors && selectedAnalyzerId > 0 && (
          <Grid item>
            <AnalyzerModeBar
              selectedAnalyzerId={selectedAnalyzerId}
              activeSnapshotId={selectedSnapshotId}
              showAnalyzerState
            />
          </Grid>
        )}
        {analyzer?.mode === AnalyzerMode.POLLING && (
          <Grid item>
            <TotalSweepTime analyzerId={selectedAnalyzerId} />
          </Grid>
        )}
        <Grid item xs style={{ overflow: 'auto' }}>
          <List dense>
            {filterSnapshots().map((snapshot) => {
              const labelId = `checkbox-list-secondary-label-${snapshot.name}`;
              return (
                <ListItem
                  key={snapshot.id}
                  button
                  onClick={() => handleSnapshotClicked(snapshot)}
                  selected={selectedSnapshotId === snapshot.id}
                >
                  {useModuleSelectors && (
                    <ListItemIcon>
                      <Checkbox
                        edge='start'
                        name={snapshot.id.toString()}
                        onChange={handleSnapshotChecked}
                        checked={checkIfSnapshotChecked(snapshot)}
                        disabled={isViewer || analyzer?.in_time_share}
                      />
                    </ListItemIcon>
                  )}
                  <ListItemText id={labelId} primary={snapshot.name} />
                  {snapshot?.crosspol_snapshot_id &&
                    snapshot.crosspol_snapshot_id > 0 && (
                      <ListItemIcon>
                        <Tooltip title='Snapshot is in cross-pol mode'>
                          <SyncIcon />
                        </Tooltip>
                      </ListItemIcon>
                    )}
                  {!disableAnalyzerSelect && (
                    <ListItemIcon
                      onClick={() => {
                        dispatch(
                          subscribeToAnalyzers({
                            user_id: user!.id,
                            analyzer_ids: [selectedAnalyzerId],
                          })
                        );
                        setSingleTraceViewSnapshotShotId(snapshot.id);
                        setSingleTraceViewModalOpen(true);
                      }}
                    >
                      <Tooltip title='Open Snapshot' placement='left'>
                        <BrokenImageOutlinedIcon />
                      </Tooltip>
                    </ListItemIcon>
                  )}
                  {snapshot.lock_user_id && <LockOutlined />}
                  <ListItemSecondaryAction></ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </Grid>
      </Grid>
    </>
  );
};

export default AnalyzerSnapshotsSelector;
